/**
 * Role selection (spec §6.8): translucent blue selection cards with a visible
 * selected outline and check. The choice here only decides which login or
 * register page opens — the authoritative role lives server-side on the
 * profile row and is enforced by RLS.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui'
import { CheckIcon, UserIcon, UsersIcon } from '@/components/icons'
import { PATHS } from '@/app/router/paths'
import type { PortalRole } from '../authContext'
import { AuthLayout, AuthLegalFooter, RustWordmark } from './AuthLayout'

const ROLE_OPTIONS: { value: PortalRole; title: string; description: string; Icon: typeof UserIcon }[] = [
  {
    value: 'client',
    title: 'Cliënt',
    description: 'Ik gebruik RUST voor mijn eigen dag, planning en herinneringen.',
    Icon: UserIcon,
  },
  {
    value: 'caregiver',
    title: 'Begeleider',
    description: 'Ik begeleid cliënten en volg hun agenda en innames.',
    Icon: UsersIcon,
  },
]

export default function RolePage() {
  const navigate = useNavigate()
  const [selected, setSelected] = useState<PortalRole | null>(null)

  const loginPath =
    selected === 'caregiver' ? PATHS.auth.caregiverLogin : PATHS.auth.clientLogin
  const registerPath =
    selected === 'caregiver' ? PATHS.auth.caregiverRegister : PATHS.auth.clientRegister

  return (
    <AuthLayout backTo={PATHS.onboarding} backLabel="Terug naar uitleg">
      <RustWordmark width={140} />
      <h1 className="auth-headline" style={{ fontSize: 26 }}>
        Hoe ga je RUST gebruiken?
      </h1>
      <p className="auth-subtitle">Kies wat bij jou past. Je rol wordt veilig opgeslagen.</p>

      <div
        role="radiogroup"
        aria-label="Kies je rol"
        style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {ROLE_OPTIONS.map(({ value, title, description, Icon }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected === value}
            className="auth-select-card"
            data-selected={selected === value}
            onClick={() => setSelected(value)}
          >
            <Icon size={28} aria-hidden="true" />
            <span>
              <span className="auth-select-card-title">{title}</span>
              <span className="auth-select-card-desc" style={{ display: 'block' }}>
                {description}
              </span>
            </span>
            <span className="auth-select-card-check" aria-hidden="true">
              {selected === value && <CheckIcon size={16} />}
            </span>
          </button>
        ))}
      </div>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={selected === null}
          onClick={() => navigate(loginPath)}
        >
          Doorgaan naar inloggen
        </Button>
        <Button
          variant="secondary"
          fullWidth
          disabled={selected === null}
          onClick={() => navigate(registerPath)}
        >
          Nieuw account aanmaken
        </Button>
      </div>
      <AuthLegalFooter />
    </AuthLayout>
  )
}
